import React, { useState, useEffect } from 'react';
import api from '../../services/api';

/**
 * AuthorSelector Component
 * Dropdown selector for choosing article authors
 * Displays only author names (no profile images or emails)
 */
const AuthorSelector = ({ 
  value, 
  onChange, 
  label = 'Writer',
  placeholder = 'Select one or more authors',
  multiple = true,
  error,
  required = false,
  className = ''
}) => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      
      // Fetch authors from the articles/authors endpoint
      // This endpoint returns only users with writer roles (CONTENT_WRITER, EDITOR, CONTENT_MANAGER, ADMIN, SUPER_ADMIN)
      const response = await api.get('/articles/authors');
      
      if (response.data.success && response.data.data) {
        setAuthors(response.data.data);
      } else {
        setAuthors([]);
      }
    } catch (error) {
      console.error('Failed to fetch authors:', error);
      setAuthors([]);
    } finally {
      setLoading(false);
    }
  };

  const getAuthorName = (author) => {
    // The /articles/authors endpoint returns: { id, name, email, role }
    if (author.name) {
      return author.name;
    }
    // Fallback for other data structures
    if (author.first_name && author.last_name) {
      return `${author.first_name} ${author.last_name}`;
    }
    if (author.username) {
      return author.username;
    }
    return author.email?.split('@')[0] || 'Unknown';
  };

  const filteredAuthors = authors.filter(author => {
    const name = getAuthorName(author).toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const handleSelect = (authorId) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(authorId)
        ? currentValues.filter(id => id !== authorId)
        : [...currentValues, authorId];
      onChange(newValues);
    } else {
      onChange(authorId);
      setIsOpen(false);
    }
  };

  const getSelectedNames = () => {
    if (!value) return placeholder;
    
    if (multiple) {
      const selectedAuthors = authors.filter(a => value.includes(a.id));
      if (selectedAuthors.length === 0) return placeholder;
      if (selectedAuthors.length === 1) return getAuthorName(selectedAuthors[0]);
      return `${selectedAuthors.length} authors selected`;
    } else {
      const selectedAuthor = authors.find(a => a.id === value);
      return selectedAuthor ? getAuthorName(selectedAuthor) : placeholder;
    }
  };

  const isSelected = (authorId) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(authorId);
    }
    return value === authorId;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Dropdown Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full px-4 py-2.5 text-left bg-white border rounded-xl shadow-sm 
            hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 
            transition-all duration-200 ${error ? 'border-red-500' : 'border-slate-300'}`}
        >
          <div className="flex items-center justify-between">
            <span className={`${!value || (Array.isArray(value) && value.length === 0) ? 'text-slate-400' : 'text-slate-900'}`}>
              {getSelectedNames()}
            </span>
            <svg
              className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-hidden">
            {/* Search Input */}
            <div className="p-3 border-b border-slate-200">
              <input
                type="text"
                placeholder="Search authors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Authors List */}
            <div className="overflow-y-auto max-h-64">
              {loading ? (
                <div className="p-4 text-center text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading authors...</span>
                  </div>
                </div>
              ) : filteredAuthors.length === 0 ? (
                <div className="p-6 text-center">
                  <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="mt-2 text-sm text-slate-500">
                    {searchTerm ? 'No authors match your search' : 'No authors found'}
                  </p>
                  {!searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchAuthors();
                      }}
                      className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Retry Loading
                    </button>
                  )}
                </div>
              ) : (
                <div className="py-2">
                  {filteredAuthors.map((author) => (
                    <button
                      key={author.id}
                      type="button"
                      onClick={() => handleSelect(author.id)}
                      className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors
                        ${isSelected(author.id) ? 'bg-blue-50 text-blue-700' : 'text-slate-900'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {getAuthorName(author)}
                        </span>
                        {isSelected(author.id) && (
                          <svg
                            className="w-5 h-5 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer with selected count */}
            {multiple && Array.isArray(value) && value.length > 0 && (
              <div className="p-3 border-t border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    {value.length} author{value.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange([]);
                    }}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}

      {/* Selected Authors Display (for multiple selection) */}
      {multiple && Array.isArray(value) && value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((authorId) => {
            const author = authors.find(a => a.id === authorId);
            if (!author) return null;
            
            return (
              <span
                key={authorId}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm"
              >
                {getAuthorName(author)}
                <button
                  type="button"
                  onClick={() => handleSelect(authorId)}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuthorSelector;
